import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { WorkspaceType } from "../common/types";
import { useWorkspaceRead, useAuthFetch } from "../services/api";

interface WorkspaceModuleConfig {
  enabled: string[];
  primary?: string;
  configs?: Record<string, any>;
}

interface WorkspaceContextType {
  workspace: WorkspaceType | null;
  loading: boolean;
  isOwner: boolean;
  isAdmin: boolean;
  isEditor: boolean;
  isUser: boolean;
  isOnlyOwner: boolean;
  hasBKG: boolean;
  enabledModules: string[];
  refreshWorkspace: () => Promise<void>;
}

const WorkspaceContext = createContext<WorkspaceContextType | null>(null);

export const useWorkspaceContext = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error(
      "useWorkspaceContext must be used within a WorkspaceProvider",
    );
  }
  return context;
};

export const useWorkspaceModuleConfig = () => {
  const authFetch = useAuthFetch();

  const getModuleConfig = useCallback(
    async (workspaceId: string): Promise<WorkspaceModuleConfig> => {
      try {
        return await authFetch(
          `/api/workspace/${workspaceId}/module-config`,
          "fetch module config",
        );
      } catch (error) {
        console.error("Failed to fetch module config:", error);
        // Return default config if API is not available yet
        return { enabled: [] };
      }
    },
    [authFetch],
  );

  return getModuleConfig;
};

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [workspace, setWorkspace] = useState<WorkspaceType | null>(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState({
    isOwner: false,
    isAdmin: false,
    isEditor: false,
    isUser: false,
    isOnlyOwner: false,
  });
  const [indexers, setIndexers] = useState({
    hasBKG: false,
  });
  const [enabledModules, setEnabledModules] = useState<string[]>([]);

  const { workspace_id } = useParams();
  const workspaceReadAPI = useWorkspaceRead();
  const getModuleConfig = useWorkspaceModuleConfig();

  const fetchWorkspace = useCallback(async () => {
    if (!workspace_id) {
      setLoading(false);
      return;
    }

    try {
      const data = await workspaceReadAPI(workspace_id);

      // Adopt fresh data whenever the content changed, not only when the id
      // changes. The old `prev?.id === data.id ? prev : data` guard kept the
      // stale object on every same-workspace refetch, which silently defeated
      // refreshWorkspace(): after IDA onboarding persisted the workspace
      // description/assistant_instructions, the refetch returned populated
      // fields but they were discarded, so WorkspaceSidebar's `workspaceIsReady`
      // gate never flipped and the nav stayed locked until a manual page
      // refresh (fresh mount, prev === null). Keep the reference stable only
      // when nothing actually changed, so the focus-refetch (line ~148) stays a
      // no-op and doesn't churn consumers.
      setWorkspace((prev) =>
        prev && JSON.stringify(prev) === JSON.stringify(data) ? prev : data,
      );
      setPermissions({
        isOwner: data.permissions?.role === "owner",
        isAdmin: ["owner", "admin"].includes(data.permissions?.role || ""),
        isEditor: ["owner", "admin", "editor"].includes(
          data.permissions?.role || "",
        ),
        isUser: true,
        isOnlyOwner: Boolean(data.permissions?.is_only_owner),
      });

      const hasBKG = data.indexers ? data.indexers.includes("ssi_bkg") : false;

      setIndexers({
        hasBKG,
      });

      try {
        const moduleConfig = await getModuleConfig(workspace_id);

        if (moduleConfig.enabled.length === 0) {
          const defaultModules = [];
          if (hasBKG) defaultModules.push("bkg");
          setEnabledModules(defaultModules);
        } else {
          setEnabledModules(moduleConfig.enabled);
        }
      } catch (error) {
        const defaultModules = [];
        if (hasBKG) defaultModules.push("bkg");
        setEnabledModules(defaultModules);
      }
    } catch (error) {
      console.error("WorkspaceProvider - Failed to fetch workspace:", error);
    } finally {
      setLoading(false);
    }
  }, [workspace_id, workspaceReadAPI, getModuleConfig]);

  useEffect(() => {
    setLoading(true);
    fetchWorkspace();
    // Only re-run when workspace_id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace_id]);

  // Refetch workspace state when the tab regains focus. Task #554: admin
  // flag toggles (mode, features, role changes) made in one tab or via the
  // BE should land in other tabs without a manual hard refresh. Standard
  // React Query refetchOnWindowFocus equivalent for this hand-rolled
  // provider. Cleanup on unmount.
  useEffect(() => {
    const handleFocus = () => {
      fetchWorkspace();
    };
    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [fetchWorkspace]);

  // Reflect the per-tab workspace name in the browser tab title. Reading from
  // the React context here (not localStorage) keeps each tab independent --
  // multi-tab sessions show distinct titles instead of all converging on
  // whichever workspace last loaded in any tab.
  useEffect(() => {
    const baseTitle = "X-Machine";
    document.title = workspace?.name
      ? `${baseTitle}: ${workspace.name}`
      : baseTitle;
    return () => {
      // Reset on unmount so non-workspace routes get the plain title back.
      document.title = baseTitle;
    };
  }, [workspace?.name]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspace,
        loading,
        ...permissions,
        ...indexers,
        enabledModules,
        refreshWorkspace: fetchWorkspace,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
